/**
 * Business Info Panel - Contact, Social, SEO Editor
 *
 * Side panel for editing non-visual template data:
 * contact info, social links, SEO fields, brand details.
 */

import React, { useState, useCallback } from 'react';
import {
  Building, Mail, Phone, MapPin, Globe,
  Instagram, Facebook, Twitter, Youtube, Linkedin,
  Search, ChevronDown, ChevronRight, X, FileText
} from 'lucide-react';

const TextInput = ({ label, value, onChange, placeholder, icon: Icon, type = 'text' }) => (
  <div className="py-1.5">
    <label className="text-xs text-gray-500 mb-1 block">{label}</label>
    <div className="flex items-center gap-2">
      {Icon && <Icon className="w-4 h-4 text-gray-400 shrink-0" />}
      <input
        type={type}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="flex-1 text-sm px-2.5 py-1.5 border border-gray-200 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
      />
    </div>
  </div>
);

const TextArea = ({ label, value, onChange, placeholder, rows = 3 }) => (
  <div className="py-1.5">
    <label className="text-xs text-gray-500 mb-1 block">{label}</label>
    <textarea
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={rows}
      className="w-full text-sm px-2.5 py-1.5 border border-gray-200 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none resize-none"
    />
  </div>
);

const CollapsibleSection = ({ title, icon: Icon, children, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-gray-100">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2 px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50"
      >
        <Icon className="w-4 h-4" />
        <span className="flex-1 text-left">{title}</span>
        {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
      </button>
      {open && <div className="px-4 pb-3">{children}</div>}
    </div>
  );
};

const BusinessInfoPanel = ({ content, onChange, onClose }) => {
  const brand = content?.brand || {};
  const contact = content?.contact || {};
  const social = content?.social || {};
  const seo = content?.seo || {};

  const update = useCallback((section, key, value) => {
    const newContent = { ...content };
    if (!newContent[section]) newContent[section] = {};
    newContent[section] = { ...newContent[section], [key]: value };
    onChange(newContent);
  }, [content, onChange]);

  return (
    <div className="w-80 bg-white border-l border-gray-200 h-full overflow-y-auto">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
        <h3 className="text-sm font-semibold text-gray-800">Your Brand Details</h3>
        {onClose && (
          <button onClick={onClose} className="p-1 hover:bg-gray-200 rounded">
            <X className="w-4 h-4 text-gray-500" />
          </button>
        )}
      </div>

      {/* Brand */}
      <CollapsibleSection title="Brand" icon={Building}>
        <TextInput
          label="Brand Name"
          value={brand.name}
          onChange={(v) => update('brand', 'name', v)}
          placeholder="Your brand name"
          icon={Building}
        />
        <TextInput
          label="Tagline"
          value={brand.tagline}
          onChange={(v) => update('brand', 'tagline', v)}
          placeholder="Your brand tagline"
        />
        <TextArea
          label="Description"
          value={brand.description}
          onChange={(v) => update('brand', 'description', v)}
          placeholder="Brief description of your brand"
          rows={2}
        />
      </CollapsibleSection>

      {/* Contact */}
      <CollapsibleSection title="Contact" icon={Phone}>
        <TextInput
          label="Email"
          value={contact.email}
          onChange={(v) => update('contact', 'email', v)}
          placeholder="contact@example.com"
          icon={Mail}
          type="email"
        />
        <TextInput
          label="Phone"
          value={contact.phone}
          onChange={(v) => update('contact', 'phone', v)}
          placeholder="+91 98765 43210"
          icon={Phone}
        />
        <TextArea
          label="Address"
          value={contact.address}
          onChange={(v) => update('contact', 'address', v)}
          placeholder="Your business address"
          rows={2}
        />
        <TextInput
          label="Website"
          value={contact.website}
          onChange={(v) => update('contact', 'website', v)}
          placeholder="https://yourdomain.com"
          icon={Globe}
        />
      </CollapsibleSection>

      {/* Social Links */}
      <CollapsibleSection title="Social Media" icon={Instagram}>
        <TextInput
          label="Instagram"
          value={social.instagram}
          onChange={(v) => update('social', 'instagram', v)}
          placeholder="https://instagram.com/yourbrand"
          icon={Instagram}
        />
        <TextInput
          label="Facebook"
          value={social.facebook}
          onChange={(v) => update('social', 'facebook', v)}
          placeholder="https://facebook.com/yourbrand"
          icon={Facebook}
        />
        <TextInput
          label="Twitter / X"
          value={social.twitter}
          onChange={(v) => update('social', 'twitter', v)}
          placeholder="https://x.com/yourbrand"
          icon={Twitter}
        />
        <TextInput
          label="YouTube"
          value={social.youtube}
          onChange={(v) => update('social', 'youtube', v)}
          placeholder="https://youtube.com/@yourbrand"
          icon={Youtube}
        />
        <TextInput
          label="LinkedIn"
          value={social.linkedin}
          onChange={(v) => update('social', 'linkedin', v)}
          placeholder="https://linkedin.com/company/yourbrand"
          icon={Linkedin}
        />
      </CollapsibleSection>

      {/* Search Engine Settings */}
      <CollapsibleSection title="Google Search Settings" icon={Search} defaultOpen={false}>
        <TextInput
          label="Page Title (shown in Google)"
          value={seo.title}
          onChange={(v) => update('seo', 'title', v)}
          placeholder="e.g., Your Brand - Designer Fashion"
          icon={FileText}
        />
        <TextArea
          label="Short Description (shown below title in Google)"
          value={seo.description}
          onChange={(v) => update('seo', 'description', v)}
          placeholder="Describe your brand in 1-2 sentences (150-160 characters work best)"
          rows={3}
        />
        <TextInput
          label="Keywords (comma separated)"
          value={seo.keywords}
          onChange={(v) => update('seo', 'keywords', v)}
          placeholder="fashion, designer, clothing, your city"
        />
      </CollapsibleSection>
    </div>
  );
};

export default BusinessInfoPanel;
